// SPDX-FileCopyrightText: Copyright Orangebot, Inc. and Medplum contributors
// SPDX-License-Identifier: Apache-2.0

/**
 * Dynamic Mantine Provider
 *
 * Wraps MantineProvider with dynamic theme support based on AppThemeContext.
 * Merges the base theme configuration with the current app theme's color overrides.
 */

import { MantineProvider, createTheme, mergeThemeOverrides } from '@mantine/core';
import type { JSX, ReactNode } from 'react';
import { useAppTheme } from '../contexts/AppThemeContext';

// Base theme configuration (non-color settings)
const baseTheme = createTheme({
  // Clean system font stack
  fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, Helvetica, Arial, sans-serif',
  headings: {
    fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, Helvetica, Arial, sans-serif',
    sizes: {
      h1: { fontSize: '1.25rem', fontWeight: '600', lineHeight: '1.4' },
      h2: { fontSize: '1.125rem', fontWeight: '600', lineHeight: '1.4' },
      h3: { fontSize: '1rem', fontWeight: '600', lineHeight: '1.4' },
      h4: { fontSize: '0.875rem', fontWeight: '600', lineHeight: '1.4' },
    },
  },
  fontSizes: {
    xs: '0.6875rem',
    sm: '0.8125rem',
    md: '0.875rem',
    lg: '0.9375rem',
    xl: '1rem',
  },
  radius: {
    xs: '2px',
    sm: '3px',
    md: '4px',
    lg: '6px',
    xl: '8px',
  },
  spacing: {
    xs: '4px',
    sm: '8px',
    md: '12px',
    lg: '16px',
    xl: '24px',
  },
  components: {
    Paper: {
      defaultProps: {
        shadow: undefined,
        withBorder: false,
      },
    },
    Card: {
      defaultProps: {
        shadow: undefined,
        withBorder: false,
      },
    },
    Button: {
      defaultProps: {
        radius: 'sm',
      },
    },
    ActionIcon: {
      defaultProps: {
        radius: 'sm',
      },
    },
    TextInput: {
      defaultProps: {
        radius: 'sm',
      },
    },
    Select: {
      defaultProps: {
        radius: 'sm',
      },
    },
    Tabs: {
      defaultProps: {
        radius: 0,
      },
    },
  },
});

interface DynamicMantineProviderProps {
  readonly children: ReactNode;
}

export function DynamicMantineProvider({ children }: DynamicMantineProviderProps): JSX.Element {
  const { mantineThemeOverride } = useAppTheme();

  // Merge base theme with dynamic color overrides
  const theme = mergeThemeOverrides(baseTheme, mantineThemeOverride);

  return <MantineProvider theme={theme}>{children}</MantineProvider>;
}
